import type { ScormData } from '~/utils/scorm'
import { useMutation, useQuery } from '@apollo/client/react'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import Spinner from '~/components/Spinner'
import { GQL_START_SCORM_ATTEMPT, GQL_UPSERT_SCORM_TRACKING } from '~/gql/scorm/scorm'
import { GQL_MY_SESSIONS } from '~/gql/sessions/sessions'
import { useAuth } from '~/helpers/store/auth'
import LearnerUserMenu from '~/pages/components/LearnerUserMenu'
import LearnerPlayerHeader from '~/pages/scorm/components/LearnerPlayerHeader'
import PlayerFrame from '~/pages/scorm/components/PlayerFrame'
import ScoFinished from '~/pages/scorm/components/ScoFinished'
import ScoPicker from '~/pages/scorm/components/ScoPicker'
import { playerInitialData, scoInitialData } from './player-initial-data'
import { nextSco } from './sco-navigation'
import ScormShell from './ScormShell'
import { useChapterPlayer } from './use-chapter-player'

const SAVE_TIMEOUT_MS = 20_000

function ScormPlayer() {
  const { t } = useTranslation()
  const { sessionId, moduleId } = useParams()

  // Pendant une impersonation, le module se joue mais rien n'est persisté :
  // la progression de l'admin ne doit pas polluer les stats de l'apprenant.
  // L'API rejette de toute façon les upserts des sessions impersonées.
  const isImpersonating = useAuth(s => Boolean(s.payload?.impersonatorId))

  // Résout le module via les parcours de l'apprenant (gating implicite : un
  // parcours non inscrit n'apparaît pas ici).
  const { data: sessionsData, loading: sessionsLoading } = useQuery(GQL_MY_SESSIONS, {
    fetchPolicy: 'cache-and-network',
  })
  const session = sessionsData?.mySessions?.find(s => s.id === sessionId)
  const step = session?.modules?.find(m => m.module?.id === moduleId)

  // Un module fermé ne se joue pas, même si son URL est devinée. L'API refuse
  // déjà le contenu ET l'écriture de progression (#182) ; ce test-ci évite
  // seulement d'afficher un lecteur qui ne chargerait rien.
  const module = step?.unlocked ? step.module : undefined

  // Terminer ce module clôt le parcours si tous les AUTRES sont déjà finis.
  // Le calcul se fait ici et pas à partir de `session.progress` : au moment où
  // l'écran de fin s'affiche, le cache `mySessions` date d'avant le dernier
  // commit et dirait encore « en cours ». Un parcours à un seul module ne
  // compte pas — féliciter pour « toute la formation » y serait grandiloquent.
  const steps = session?.modules ?? []
  const completesPath = steps.length > 1
    && steps.every(s => s.module?.id === moduleId || s.myTracking?.completionStatus === 'COMPLETED')

  // Ouvrir le module, c'est reprendre la tentative en cours, ou démarrer la
  // suivante si elle est terminée (#84) : l'API en décide, et refuse quand le
  // parcours interdit de refaire. Rejouée sans risque (StrictMode) : un second
  // appel trouve la nouvelle tentative non terminée et la reprend.
  const [startAttempt, { data, loading: attemptLoading, error: attemptError, called }] = useMutation(GQL_START_SCORM_ATTEMPT)
  useEffect(() => {
    if (sessionId && moduleId) void startAttempt({ variables: { sessionId, moduleId } }).catch(() => {})
  }, [startAttempt, sessionId, moduleId])
  const retakeRefused = attemptError?.message.includes('MODULE_RETAKE_NOT_ALLOWED') ?? false
  // Tentative sur laquelle ce lecteur écrit : renvoyée à chaque sauvegarde, pour
  // qu'un onglet resté ouvert n'écrive pas dans la tentative démarrée ailleurs.
  const attemptNumber = data?.startScormAttempt?.attemptNumber ?? 1

  const [upsertTracking] = useMutation(GQL_UPSERT_SCORM_TRACKING)

  // Module à plusieurs SCO (#160) : un chapitre à la fois, chacun suivi à part.
  const chapters = useChapterPlayer({ sessionId, moduleId }, data?.startScormAttempt)
  const { playing, markCompleted, switchTo } = chapters

  const handleCommit = useCallback(async (scormData: ScormData) => {
    if (!sessionId || !moduleId || isImpersonating) return
    // Le module est désigné explicitement : sans lui, l'API rattacherait la
    // progression au PREMIER module du parcours, et les cinq modules d'un
    // parcours écriraient tous sur la même ligne.
    const input: { sessionId: string, moduleId: string, [key: string]: unknown } = { sessionId, moduleId, attemptNumber }
    if (playing) input.scoIdentifier = playing.identifier

    if (scormData.completionStatus) input.completionStatus = scormData.completionStatus
    if (scormData.successStatus) input.successStatus = scormData.successStatus
    if (scormData.scoreRaw != null) input.scoreRaw = scormData.scoreRaw
    if (scormData.scoreMin != null) input.scoreMin = scormData.scoreMin
    if (scormData.scoreMax != null) input.scoreMax = scormData.scoreMax
    if (scormData.scoreScaled != null) input.scoreScaled = scormData.scoreScaled
    if (scormData.totalTime) input.totalTime = scormData.totalTime
    if (scormData.sessionTime) input.sessionTime = scormData.sessionTime
    if (scormData.suspendData) input.suspendData = scormData.suspendData
    if (scormData.location) input.location = scormData.location
    if (scormData.interactions) input.interactions = scormData.interactions

    // Les sauvegardes partent l'une après l'autre (#158) : une requête qui ne
    // répond jamais bloquerait toutes les suivantes. Passé ce délai, elle échoue
    // et ce qu'elle portait repart avec la prochaine.
    await upsertTracking({ variables: { input }, context: { fetchOptions: { signal: AbortSignal.timeout(SAVE_TIMEOUT_MS) } } })
    if (playing && scormData.completionStatus === 'COMPLETED') markCompleted(playing.identifier)
  }, [upsertTracking, sessionId, moduleId, isImpersonating, attemptNumber, playing, markCompleted])

  const initialData = useMemo(
    () => playing ? scoInitialData(chapters.resume, playing.identifier) : playerInitialData(data?.startScormAttempt),
    [data, chapters.resume, playing],
  )

  const header = (
    <LearnerPlayerHeader
      sessionName={session?.name}
      stepPosition={step?.position}
      totalSteps={session?.modules?.length ?? 0}
      impersonating={isImpersonating}
    />
  )
  const headerRight = (
    <>
      {playing && <ScoPicker scos={chapters.scos} active={playing.identifier} completed={chapters.completed} onSelect={id => void switchTo(id)} />}
      <LearnerUserMenu />
    </>
  )

  // Changement de chapitre en cours : le bandeau reste, seul le contenu attend.
  if (chapters.switching) {
    return (
      <PlayerFrame fallbackTo="/my-sessions" fallbackLabel={t('scorm.player.backToSessions')} headerContent={header} headerRight={headerRight}>
        <div className="flex h-full items-center justify-center bg-background">
          <Spinner className="size-6 text-muted-foreground" label={t('scorm.chapters.switching')} />
        </div>
      </PlayerFrame>
    )
  }

  return (
    <ScormShell
      // Un chapitre = un lancement : changer de chapitre remonte un bridge neuf.
      key={playing?.identifier ?? 'module'}
      bridgeRef={chapters.bridgeRef}
      loading={sessionsLoading || attemptLoading || chapters.loading || (!called && Boolean(sessionId && moduleId))}
      // Échec à l'ouverture : ne JAMAIS lancer le paquet vierge — sa première
      // sauvegarde écraserait la reprise d'un module en cours.
      module={attemptError || !module ? undefined : { ...module, launchUrl: playing?.href ?? module.launchUrl }}
      initialData={initialData}
      onCommit={handleCommit}
      headerContent={header}
      headerRight={headerRight}
      fallbackTo="/my-sessions"
      fallbackLabel={t('scorm.player.backToSessions')}
      unavailableMessage={retakeRefused ? t('scorm.player.retakeNotAllowed') : t('scorm.player.unavailable')}
      completesPath={completesPath}
      renderFinished={playing && (info => (
        <ScoFinished
          completed={info.completed}
          completesPath={completesPath}
          next={nextSco(chapters.scos, chapters.completed, playing.identifier)}
          onNext={id => void switchTo(id)}
          fallbackTo="/my-sessions"
          fallbackLabel={t('scorm.player.backToSessions')}
        />
      ))}
    />
  )
}

export default ScormPlayer
